import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { CashierTradingAccount } from "@/api/cashier";
import { AccountList } from "@/components/account-list/AccountList";
import { ErrorPage } from "@/components/error-page/ErrorPage";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { Section } from "@/components/section/Section";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useLogger } from "@/hooks/useLogger";
import { useTradingAccounts } from "@/hooks/useTradingAccounts";
import { WithdrawalNotAvailable } from "@/pages/withdrawals/accounts/WithdrawalNotAvailable";
import { GLOBAL_NAVIGATION_URLS, globalNavigation } from "@/utils/constants";
import { ECashierFlows, ERoutes } from "@/utils/enums";
import { serializeError } from "@/utils/log";
import style from "./style.module.css";

const isAccountWithdrawalDisabled = (account: CashierTradingAccount) =>
  !account.isEnabled || account.isTemporary;

export const Accounts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logger = useLogger();
  const {
    data: tradingAccountList,
    error,
    isLoading,
    refetch,
  } = useTradingAccounts();
  const { setSelectedAccount, selectedAccount } = useAccount();

  // Refetch account balances on mount since the query in CustomerDataProvider never unmounts
  useEffect(() => {
    refetch();
  }, [refetch]);

  if (error) {
    logger.error("Failed to load withdrawal trading accounts", {
      error: serializeError(error),
    });
    return (
      <ErrorPage buttonAction={() => navigate(`/${ECashierFlows.Withdraw}`)} />
    );
  }

  if (
    !isLoading &&
    (!tradingAccountList?.length ||
      tradingAccountList.every(isAccountWithdrawalDisabled))
  ) {
    logger.error("Withdrawal not available, no valid trading accounts found", {
      tradingAccountList,
    });
    return <WithdrawalNotAvailable reason="no_accounts" />;
  }

  return (
    <div className={"ps-flex ps-flex-col ps-gap-8"}>
      <Section title={t("withdrawals.accounts.select_account")}>
        <AccountList
          isLoading={isLoading}
          accountList={tradingAccountList ?? []}
          selectedAccountChange={setSelectedAccount}
          initialSelectedAccount={selectedAccount}
          accountDisabled={isAccountWithdrawalDisabled}
        />
      </Section>
      <NavButtons
        className={style.navButtons}
        backAction={() =>
          globalNavigation(GLOBAL_NAVIGATION_URLS.TRADING_ACCOUNTS, true)
        }
        nextLabel={t("global.nav_buttons.next")}
        nextAction={() => navigate(`../${ERoutes.ChooseMethod}`)}
        nextDisabled={!selectedAccount}
      />
    </div>
  );
};
